import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { worldCupMatchDataApi } from '../api';
import type {
  MatchDataKpi,
  MatchesByStage,
  MatchStageParam,
  MatchSyncStatus,
  PatchWorldCupMatchPayload,
} from '../types';

type MatchDataState = {
  kpi: MatchDataKpi | null;
  matchesByStage: MatchesByStage | null;
  syncStatus: MatchSyncStatus | null;
};

const REFRESH_INTERVAL = 30 * 1000;

export const useWorldCupMatchData = (stage?: MatchStageParam) => {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<MatchDataState>({
    kpi: null,
    matchesByStage: null,
    syncStatus: null,
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(
    async (options?: { silent?: boolean }) => {
      abortRef.current?.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      if (options?.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);

      try {
        const [kpiResult, matchesResult, syncStatusResult] = await Promise.allSettled([
          worldCupMatchDataApi.getKpi(controller.signal),
          worldCupMatchDataApi.getMatches(stage, controller.signal),
          worldCupMatchDataApi.getSyncStatus(controller.signal),
        ]);

        if (!mountedRef.current) return;

        setData((prev) => ({
          kpi: kpiResult.status === 'fulfilled' ? kpiResult.value : prev.kpi,
          matchesByStage:
            matchesResult.status === 'fulfilled' ? matchesResult.value : prev.matchesByStage,
          syncStatus:
            syncStatusResult.status === 'fulfilled' ? syncStatusResult.value : prev.syncStatus,
        }));

        const failed = [kpiResult, matchesResult, syncStatusResult].find(
          (item) => item.status === 'rejected'
        );

        if (failed && failed.status === 'rejected') {
          const reason = failed.reason;
          setError(reason instanceof Error ? reason : new Error(String(reason?.message || reason)));
        }
      } catch (err: any) {
        if (!mountedRef.current) return;

        if (err?.name !== 'AbortError') {
          setError(err instanceof Error ? err : new Error(String(err?.message || err)));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [stage]
  );

  const resync = useCallback(async () => {
    setResyncing(true);

    try {
      const result = await worldCupMatchDataApi.resync();
      await fetchData({ silent: true });

      return result;
    } finally {
      if (mountedRef.current) {
        setResyncing(false);
      }
    }
  }, [fetchData]);

  const patchMatch = useCallback(
    async (matchId: string, payload: PatchWorldCupMatchPayload) => {
      const result = await worldCupMatchDataApi.patchMatch(matchId, payload);
      await fetchData({ silent: true });
      return result;
    },
    [fetchData]
  );

  useEffect(() => {
    mountedRef.current = true;

    fetchData();

    const timer = window.setInterval(() => {
      fetchData({ silent: true });
    }, REFRESH_INTERVAL);

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      window.clearInterval(timer);
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      ...data,
      loading,
      refreshing,
      resyncing,
      error,
      refetch: () => fetchData({ silent: true }),
      resync,
      patchMatch,
    }),
    [data, loading, refreshing, resyncing, error, fetchData, resync, patchMatch]
  );
};
